// app/Router.tsx — All application routes with lazy loading

import { lazy } from 'react';
import { createBrowserRouter } from 'react-router-dom';

// ── Lazy load ALL feature screens for code splitting ──
const WelcomeScreen      = lazy(() => import('@/features/onboarding/screens/WelcomeScreen'));
const CreateTreeScreen   = lazy(() => import('@/features/onboarding/screens/CreateTreeScreen'));
const ClaimNodeScreen    = lazy(() => import('@/features/onboarding/screens/ClaimNodeScreen'));
const LoginScreen        = lazy(() => import('@/features/auth/screens/LoginScreen'));
const RegisterScreen     = lazy(() => import('@/features/auth/screens/RegisterScreen'));

const HomeScreen         = lazy(() => import('@/features/home/screens/HomeScreen'));

const BookIndexScreen    = lazy(() => import('@/features/tree/screens/BookIndexScreen'));
const TreeCanvasScreen   = lazy(() => import('@/features/tree/screens/TreeCanvasScreen'));
const BirdsEyeScreen     = lazy(() => import('@/features/tree/screens/BirdsEyeScreen'));
const AddRelativeScreen  = lazy(() => import('@/features/tree/screens/AddRelativeScreen'));
const ReviewFamilyScreen = lazy(() => import('@/features/tree/screens/ReviewFamilyScreen'));
const EditNodeScreen     = lazy(() => import('@/features/tree/screens/EditNodeScreen'));
const NodeViewScreen     = lazy(() => import('@/features/tree/screens/NodeViewScreen'));

const ProfileViewScreen  = lazy(() => import('@/features/profile/screens/ProfileViewScreen'));
const EditProfileScreen  = lazy(() => import('@/features/profile/screens/EditProfileScreen'));
const PrivacySettings    = lazy(() => import('@/features/profile/screens/PrivacySettingsScreen'));

const ConversationList   = lazy(() => import('@/features/messaging/screens/ConversationListScreen'));
const ChatThread         = lazy(() => import('@/features/messaging/screens/ChatThreadScreen'));

const MergeRequest       = lazy(() => import('@/features/merge/screens/MergeRequestScreen'));
const ConflictResolution = lazy(() => import('@/features/merge/screens/ConflictResolutionScreen'));

const InnerCircleHub     = lazy(() => import('@/features/inner-circle/screens/HubScreen'));
const InnerCirclePending = lazy(() => import('@/features/inner-circle/screens/PendingRequestsScreen'));
const InnerCircleAdmin   = lazy(() => import('@/features/inner-circle/screens/AdminSettingsScreen'));
const InnerCircleSaved   = lazy(() => import('@/features/inner-circle/screens/SavedScreen'));
const MatrimonyScreen    = lazy(() => import('@/features/inner-circle/screens/matrimony/MatrimonyScreen'));
const MatrimonyForm      = lazy(() => import('@/features/inner-circle/screens/matrimony/ProfileFormScreen'));
const MatrimonyDraftFor  = lazy(() => import('@/features/inner-circle/screens/matrimony/DraftForRelativeScreen'));
const JobsScreen         = lazy(() => import('@/features/inner-circle/screens/jobs/JobsScreen'));
const ServicesScreen     = lazy(() => import('@/features/inner-circle/screens/services/ServicesScreen'));

const TreeHistory        = lazy(() => import('@/features/history/screens/TreeHistoryScreen'));
const NotificationsScreen= lazy(() => import('@/features/notifications/screens/NotificationsScreen'));
const SettingsScreen     = lazy(() => import('@/features/settings/screens/SettingsScreen'));
const InviteManagement   = lazy(() => import('@/features/settings/screens/InviteManagementScreen'));
const ReceivedInvites    = lazy(() => import('@/features/settings/screens/ReceivedInvitesScreen'));
const AIChatScreen       = lazy(() => import('@/features/ai-chat/screens/AIChatScreen'));

// ── Layout components (not lazy — part of the shell) ──
import { PageShell } from '@/components/layout/PageShell';
import { AuthGuard } from '@/components/layout/AuthGuard';
import { RouteSuspense } from '@/components/layout/RouteSuspense';
import { DefaultTreeRedirect } from '@/components/layout/DefaultTreeRedirect';

export const router = createBrowserRouter([
  // ── Public routes (no auth required) ──
  {
    path: '/welcome',
    element: <RouteSuspense><WelcomeScreen /></RouteSuspense>,
  },
  {
    path: '/login',
    element: <RouteSuspense><LoginScreen /></RouteSuspense>,
  },
  {
    path: '/register',
    element: <RouteSuspense><RegisterScreen /></RouteSuspense>,
  },
  {
    path: '/onboarding/create',
    element: <RouteSuspense><CreateTreeScreen /></RouteSuspense>,
  },
  {
    path: '/invite/:token',
    element: <RouteSuspense><ClaimNodeScreen /></RouteSuspense>,
  },

  // ── Protected routes (wrapped in AuthGuard + PageShell) ──
  {
    element: (
      <AuthGuard>
        <PageShell />
      </AuthGuard>
    ),
    children: [
      // Default → last-visited tree
      { index: true, element: <DefaultTreeRedirect /> },

      // Home — browse/switch/create trees
      { path: 'home', element: <RouteSuspense><HomeScreen /></RouteSuspense> },

      // Tree — scoped by :treeId so each tree has its own bookmarkable URL.
      // The bare path is the book's index (table of contents); the canvas
      // itself is one level in, at /canvas.
      { path: 'trees/:treeId',               element: <RouteSuspense><BookIndexScreen /></RouteSuspense> },
      { path: 'trees/:treeId/canvas',         element: <RouteSuspense><TreeCanvasScreen /></RouteSuspense> },
      { path: 'trees/:treeId/overview',      element: <RouteSuspense><BirdsEyeScreen /></RouteSuspense> },
      { path: 'trees/:treeId/add',           element: <RouteSuspense><AddRelativeScreen /></RouteSuspense> },
      { path: 'trees/:treeId/review-family', element: <RouteSuspense><ReviewFamilyScreen /></RouteSuspense> },
      { path: 'trees/:treeId/edit-node',     element: <RouteSuspense><EditNodeScreen /></RouteSuspense> },
      { path: 'trees/:treeId/node/:nodeId',  element: <RouteSuspense><NodeViewScreen /></RouteSuspense> },
      { path: 'trees/:treeId/invites',       element: <RouteSuspense><InviteManagement /></RouteSuspense> },

      // Profile
      { path: 'profile',            element: <RouteSuspense><ProfileViewScreen /></RouteSuspense> },
      { path: 'profile/:userId',    element: <RouteSuspense><ProfileViewScreen /></RouteSuspense> },
      { path: 'profile/edit',       element: <RouteSuspense><EditProfileScreen /></RouteSuspense> },
      { path: 'profile/privacy',    element: <RouteSuspense><PrivacySettings /></RouteSuspense> },

      // Chat
      { path: 'chat',                    element: <RouteSuspense><ConversationList /></RouteSuspense> },
      { path: 'chat/:conversationId',    element: <RouteSuspense><ChatThread /></RouteSuspense> },

      // Merge (Phase 3)
      { path: 'merge',                        element: <RouteSuspense><MergeRequest /></RouteSuspense> },
      { path: 'merge/:requestId/resolve',     element: <RouteSuspense><ConflictResolution /></RouteSuspense> },

      // Inner Circle — matrimony, jobs, services. Global (bottom-nav icon,
      // unchanged): a consolidated pool across every community on the
      // platform. Book-scoped (reached from a Samaj book's index): the same
      // screens, mounted one level under that tree, narrowed to its own
      // pool — see useInnerCircleScope for how the same components serve
      // both without duplication.
      { path: 'inner-circle',                     element: <RouteSuspense><InnerCircleHub /></RouteSuspense> },
      { path: 'inner-circle/requests',             element: <RouteSuspense><InnerCirclePending /></RouteSuspense> },
      { path: 'inner-circle/admin',                 element: <RouteSuspense><InnerCircleAdmin /></RouteSuspense> },
      { path: 'inner-circle/saved',                 element: <RouteSuspense><InnerCircleSaved /></RouteSuspense> },
      { path: 'inner-circle/matrimony',            element: <RouteSuspense><MatrimonyScreen /></RouteSuspense> },
      { path: 'inner-circle/matrimony/new',        element: <RouteSuspense><MatrimonyForm /></RouteSuspense> },
      { path: 'inner-circle/matrimony/edit/:id',   element: <RouteSuspense><MatrimonyForm /></RouteSuspense> },
      { path: 'inner-circle/matrimony/draft-for-relative', element: <RouteSuspense><MatrimonyDraftFor /></RouteSuspense> },
      { path: 'inner-circle/jobs',                 element: <RouteSuspense><JobsScreen /></RouteSuspense> },
      { path: 'inner-circle/services',             element: <RouteSuspense><ServicesScreen /></RouteSuspense> },

      { path: 'trees/:treeId/circle',                     element: <RouteSuspense><InnerCircleHub /></RouteSuspense> },
      { path: 'trees/:treeId/circle/requests',             element: <RouteSuspense><InnerCirclePending /></RouteSuspense> },
      { path: 'trees/:treeId/circle/admin',                 element: <RouteSuspense><InnerCircleAdmin /></RouteSuspense> },
      { path: 'trees/:treeId/circle/saved',                 element: <RouteSuspense><InnerCircleSaved /></RouteSuspense> },
      { path: 'trees/:treeId/circle/matrimony',            element: <RouteSuspense><MatrimonyScreen /></RouteSuspense> },
      { path: 'trees/:treeId/circle/matrimony/new',        element: <RouteSuspense><MatrimonyForm /></RouteSuspense> },
      { path: 'trees/:treeId/circle/matrimony/edit/:id',   element: <RouteSuspense><MatrimonyForm /></RouteSuspense> },
      { path: 'trees/:treeId/circle/matrimony/draft-for-relative', element: <RouteSuspense><MatrimonyDraftFor /></RouteSuspense> },
      { path: 'trees/:treeId/circle/jobs',                 element: <RouteSuspense><JobsScreen /></RouteSuspense> },
      { path: 'trees/:treeId/circle/services',             element: <RouteSuspense><ServicesScreen /></RouteSuspense> },

      // More screens
      { path: 'history',       element: <RouteSuspense><TreeHistory /></RouteSuspense> },
      { path: 'notifications', element: <RouteSuspense><NotificationsScreen /></RouteSuspense> },
      { path: 'settings',            element: <RouteSuspense><SettingsScreen /></RouteSuspense> },
      { path: 'settings/invites/me', element: <RouteSuspense><ReceivedInvites /></RouteSuspense> },
      { path: 'ai',            element: <RouteSuspense><AIChatScreen /></RouteSuspense> },
    ],
  },

  // Catch-all → redirect to last-visited tree
  { path: '*', element: <DefaultTreeRedirect /> },
]);
